import { connectBasePath } from "./base-path";

export const SESSION_COOKIE = "deb-connect-auth";

const MAX_CHUNK = 3000;
const MAX_AGE_SEC = 60 * 60 * 24 * 30;

export type CookieJar = {
  get cookie(): string;
  set cookie(value: string);
};

export function sessionCookiePath(base = connectBasePath()): string {
  return base || "/";
}

function cookieFlags(path: string, secure: boolean, maxAge: number): string {
  const secureAttr = secure ? "; Secure" : "";
  return `Path=${path}; Max-Age=${maxAge}; SameSite=Lax${secureAttr}`;
}

function parseCookieHeader(header: string): Map<string, string> {
  const map = new Map<string, string>();
  if (!header) return map;
  for (const part of header.split(";")) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const eq = trimmed.indexOf("=");
    if (eq <= 0) continue;
    map.set(trimmed.slice(0, eq), trimmed.slice(eq + 1));
  }
  return map;
}

/** Read a session cookie, including chunks written when the token pair is large. */
export function readSessionCookie(header: string, name = SESSION_COOKIE): string | null {
  const jar = parseCookieHeader(header);
  const countRaw = jar.get(`${name}.n`);
  if (countRaw) {
    const count = Number(countRaw);
    if (!Number.isInteger(count) || count <= 0) return null;
    let encoded = "";
    for (let i = 0; i < count; i++) {
      const chunk = jar.get(`${name}.${i}`);
      if (chunk == null) return null;
      encoded += chunk;
    }
    try {
      return decodeURIComponent(encoded);
    } catch {
      return null;
    }
  }
  const single = jar.get(name);
  if (!single) return null;
  try {
    return decodeURIComponent(single);
  } catch {
    return null;
  }
}

function writePair(jar: CookieJar, name: string, value: string, path: string, secure: boolean, maxAge: number) {
  jar.cookie = `${name}=${value}; ${cookieFlags(path, secure, maxAge)}`;
}

function clearName(jar: CookieJar, name: string, path: string, secure: boolean) {
  jar.cookie = `${name}=; ${cookieFlags(path, secure, 0)}`;
}

/**
 * Persist the session JSON. Clears any previous single or chunked cookie.
 * Returns false when the value cannot fit in eight chunks.
 */
export function writeSessionCookie(
  jar: CookieJar,
  value: string | null,
  opts: { path: string; secure: boolean; name?: string },
): boolean {
  const name = opts.name ?? SESSION_COOKIE;
  clearName(jar, name, opts.path, opts.secure);
  clearName(jar, `${name}.n`, opts.path, opts.secure);
  for (let i = 0; i < 8; i++) clearName(jar, `${name}.${i}`, opts.path, opts.secure);
  if (!value) return true;

  const encoded = encodeURIComponent(value);
  if (encoded.length <= MAX_CHUNK) {
    writePair(jar, name, encoded, opts.path, opts.secure, MAX_AGE_SEC);
    return true;
  }

  const chunks: string[] = [];
  for (let i = 0; i < encoded.length; i += MAX_CHUNK) {
    chunks.push(encoded.slice(i, i + MAX_CHUNK));
  }
  if (chunks.length > 8) return false;
  chunks.forEach((chunk, i) => {
    writePair(jar, `${name}.${i}`, chunk, opts.path, opts.secure, MAX_AGE_SEC);
  });
  writePair(jar, `${name}.n`, String(chunks.length), opts.path, opts.secure, MAX_AGE_SEC);
  return true;
}

export function browserCookieJar(): CookieJar | null {
  if (typeof document === "undefined") return null;
  return document;
}

export function cookieSecure(): boolean {
  return typeof location !== "undefined" && location.protocol === "https:";
}
